import { NavLink } from 'react-router-dom';
import { LEGACY_COMMANDS, type CommandGroup } from './CommandLegacyPage';

// The Command-local rail.
//
// Two kinds of destination, both reachable by URL and by link:
//
//   * The six routed pages of the Command Center — /command, /command/allowance,
//     /command/models, /command/pipeline, /command/decisions and
//     /command/blueprints.
//   * The preserved operator panels, which keep their legacy `/command/<mode>`
//     path and their tab-bar label.
//
// The group headings (Observe / Operate / Admin) are preserved from the tab bar
// so the operator still recognises the shape of Command.
//
// The two blueprint-flagged legacy modes — the operations overview and the
// NotebookLM bridge — are preserved here too. They own no panel any more: their
// content was moved to /command/blueprints by composition, never deleted. They
// still belonged to the Observe group of the old tab bar, so the rail keeps both
// of them, at the place the old bar had them, and points them at the preserved
// blueprint page.

type CommandPageStatus = 'index' | 'pending' | 'blueprint';

interface CommandPage {
  path: string;
  label: string;
  status: CommandPageStatus;
}

const COMMAND_PAGES: CommandPage[] = [
  { path: '/command', label: 'Overview', status: 'index' },
  { path: '/command/allowance', label: 'Allowance', status: 'pending' },
  { path: '/command/models', label: 'Models', status: 'pending' },
  { path: '/command/pipeline', label: 'Pipeline', status: 'pending' },
  { path: '/command/decisions', label: 'Decisions', status: 'pending' },
  { path: '/command/blueprints', label: 'Blueprints', status: 'blueprint' },
];

const LEGACY_GROUPS: CommandGroup[] = ['Observe', 'Operate', 'Admin'];

function linkClass(isActive: boolean): string {
  return [
    'flex items-center justify-between gap-2 rounded px-2 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors',
    isActive
      ? 'bg-gray-800 text-[#39FF14] border border-gray-700'
      : 'text-gray-400 hover:text-gray-200 hover:border-gray-800 border border-transparent',
  ].join(' ');
}

export function CommandRail() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-1 lg:gap-4">
      <div>
        <p className="mb-2 text-[9px] font-mono uppercase tracking-[0.2em] text-gray-400">Command</p>
        <ul className="flex flex-col gap-1">
          {COMMAND_PAGES.map((page) => (
            <li key={page.path}>
              <NavLink
                to={page.path}
                end={page.path === '/command'}
                className={({ isActive }) => linkClass(isActive)}
                title={
                  page.status === 'blueprint'
                    ? 'Preserved blueprint: not all underlying data is wired yet'
                    : undefined
                }
              >
                <span>{page.label}</span>
                {page.status === 'pending' && (
                  <span className="shrink-0 text-[9px] uppercase tracking-wider text-gray-400">pending</span>
                )}
                {page.status === 'blueprint' && (
                  <span aria-hidden="true" className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70">
                    B
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {LEGACY_GROUPS.map((group) => {
        const entries = LEGACY_COMMANDS.filter((entry) => entry.group === group);
        if (entries.length === 0) return null;
        return (
          <div key={group}>
            <p className="mb-2 text-[9px] font-mono uppercase tracking-[0.2em] text-gray-400">{group}</p>
            <ul className="flex flex-col gap-1">
              {entries.map((entry) => {
                // A mode without a panel is a blueprint-flagged legacy mode: its
                // content was moved to /command/blueprints by composition, so the
                // rail keeps the mode, keeps its tab-bar label, and sends it to
                // the preserved blueprint page.
                const isBlueprint = !entry.panel;
                return (
                  <li key={entry.mode}>
                    <NavLink
                      to={entry.canonical}
                      className={({ isActive }) => linkClass(isActive)}
                      title={
                        isBlueprint
                          ? `Preserved blueprint: the legacy ${entry.label} tab now lives on /command/blueprints`
                          : undefined
                      }
                    >
                      {/* The legacy mode leads the label because the routed
                          Command overview above already reads "Overview": the
                          mode is what tells the two apart. */}
                      <span>{isBlueprint ? `${entry.mode} · ${entry.label}` : entry.label}</span>
                      {isBlueprint && (
                        <span
                          aria-hidden="true"
                          className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70"
                        >
                          B
                        </span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
